import React, { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import toast from '../../../utils/toast';
import { useAuth } from '../../../context/auth-provider';
import { deskInPlan } from '../../../utils/products';
import { RightArrow } from '../../icons/right-arrow';
import {
  Field,
  FieldError,
  FieldRow,
  Label,
  Input,
  PasswordInput,
  FullWidthButton,
  ErrorText,
  HelperText,
} from '../../onboarding/onboarding.styled';
import { Shake, StatusLabel } from '../../../motion';
import {
  Profile,
  cancelEmailChange,
  fileToAvatarDataUrl,
  getProfile,
  requestEmailChange,
  updateProfile,
} from '../../../api/profile-client';
import {
  AvatarActions,
  AvatarPreview,
  AvatarRow,
  FileButton,
  PendingBanner,
  QuietButton,
  SectionHeading,
  SectionLead,
} from './account.styled';

/**
 * EDIT YOUR OWN ACCOUNT.
 *
 * The gap this fills: the account menu offered Change Password, Devices and
 * Sign Out, and nothing else. A name mistyped at signup — or, far more often,
 * one typed FOR somebody by whoever sent their invitation — was permanent, for
 * every role in the product.
 *
 * ── WHY THE EMAIL IS A SEPARATE FORM WITH A PASSWORD ON IT ──
 * Name, phone and picture are profile fields. The email is the LOGIN
 * IDENTIFIER: `users.email_hash` is the blind index the sign-in route resolves
 * an account by. So it does not save with the rest, it does not save at all
 * until it is proven, and it asks for the current password first — an
 * unattended session at a front desk is a real thing, and swapping the address
 * is how five minutes at a signed-in machine becomes permanent ownership of an
 * account, because every future reset link then arrives in somebody else's
 * inbox.
 */
export const AccountProfile: React.FC = () => {
  const { user, refreshAuth } = useAuth();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState('');
  /* Refusal counters: the shake replays on every refusal, not only when the
     error first appears (the CSS keyframe it replaces could not do that). */
  const [detailsAttempt, setDetailsAttempt] = useState(0);
  const [savingDetails, setSavingDetails] = useState(false);

  const [email, setEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailAttempt, setEmailAttempt] = useState(0);
  const [savingEmail, setSavingEmail] = useState(false);

  const refuseDetails = (message: string) => {
    setDetailsError(message);
    setDetailsAttempt(count => count + 1);
  };

  const refuseEmail = (message: string) => {
    setEmailError(message);
    setEmailAttempt(count => count + 1);
  };

  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  const adopt = (next: Profile) => {
    setProfile(next);
    setFirstName(next.first_name);
    setLastName(next.last_name);
    setPhone(next.phone_number || '');
    setAvatar(next.user_image);
    setEmail(next.email);
  };

  useEffect(() => {
    getProfile()
      .then(next => {
        if (alive.current) adopt(next);
      })
      .catch(() => {
        if (alive.current) {
          setLoadError('We could not load your account details.');
        }
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  }, []);

  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) {
      refuseDetails('Enter your first name');

      return;
    }
    setDetailsError('');
    setSavingDetails(true);
    try {
      const next = await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phone.trim(),
        // Only when it actually moved. Sending the unchanged data URI on every
        // name edit would push ~10 KB back through a route that did not ask for
        // it, and sending `null` would clear a picture nobody touched.
        ...(avatar !== profile?.user_image ? { user_image: avatar } : {}),
      });
      adopt(next);
      // The header avatar and the name in the account chip both read from the
      // session, which is a cache of GET /auth — without this they keep showing
      // the old values on every screen except this one.
      await refreshAuth().catch(() => undefined);
      toast.success('Your details have been saved.');
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      refuseDetails(
        ax.response?.data?.message || 'Could not save your details.'
      );
    } finally {
      if (alive.current) setSavingDetails(false);
    }
  };

  const pickAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so choosing the SAME file twice still fires a change
    // event — otherwise a failed first attempt cannot be retried.
    e.target.value = '';
    if (!file) return;
    try {
      setAvatar(await fileToAvatarDataUrl(file));
      setDetailsError('');
    } catch (err) {
      refuseDetails((err as Error).message);
    }
  };

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = email.trim().toLowerCase();
    if (next === (profile?.email || '').toLowerCase()) {
      refuseEmail('That is already the address on this account');

      return;
    }
    if (!emailPassword) {
      refuseEmail('Enter your current password to change the address');

      return;
    }
    setEmailError('');
    setSavingEmail(true);
    try {
      adopt(await requestEmailChange(next, emailPassword));
      setEmailPassword('');
      toast.success(`Confirmation sent to ${next}.`);
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      refuseEmail(
        ax.response?.data?.message || 'Could not start the email change.'
      );
    } finally {
      if (alive.current) setSavingEmail(false);
    }
  };

  const cancelPending = async () => {
    try {
      adopt(await cancelEmailChange());
      toast.success('The pending email change was cancelled.');
    } catch {
      toast.error('Could not cancel that change.');
    }
  };

  if (loading) {
    return <HelperText>Loading your account…</HelperText>;
  }
  if (loadError) {
    return <ErrorText>{loadError}</ErrorText>;
  }

  const initial = (profile?.first_name || user?.full_name || '?')
    .charAt(0)
    .toUpperCase();

  return (
    <>
      <SectionHeading>Your details</SectionHeading>
      <SectionLead>
        {/* Independent Stays IS1: a Stays-only account has no front desk. */}
        {deskInPlan(user)
          ? 'This is the name the front desk, your colleagues and every log entry you write are shown under.'
          : 'This is the name your colleagues and your guests see.'}
      </SectionLead>

      <form onSubmit={saveDetails}>
        <AvatarRow>
          <AvatarPreview src={avatar} aria-hidden="true">
            {avatar ? '' : initial}
          </AvatarPreview>
          <AvatarActions>
            <FileButton>
              {avatar ? 'Change picture' : 'Add a picture'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={pickAvatar}
              />
            </FileButton>
            {avatar ? (
              <QuietButton type="button" onClick={() => setAvatar(null)}>
                Remove picture
              </QuietButton>
            ) : null}
          </AvatarActions>
        </AvatarRow>

        <FieldRow>
          <Field>
            <Label htmlFor="ap-first">First name</Label>
            <Shake
              active={!!detailsError && !firstName.trim()}
              attempt={detailsAttempt}
            >
              <Input
                id="ap-first"
                value={firstName}
                maxLength={80}
                autoComplete="given-name"
                hasError={!!detailsError && !firstName.trim()}
                onChange={e => setFirstName(e.target.value)}
              />
            </Shake>
          </Field>
          <Field>
            <Label htmlFor="ap-last">Last name</Label>
            <Input
              id="ap-last"
              value={lastName}
              maxLength={80}
              autoComplete="family-name"
              onChange={e => setLastName(e.target.value)}
            />
          </Field>
        </FieldRow>
        <Field>
          <Label htmlFor="ap-phone">Phone number</Label>
          <Input
            id="ap-phone"
            value={phone}
            maxLength={40}
            autoComplete="tel"
            onChange={e => setPhone(e.target.value)}
          />
        </Field>
        <FieldError message={detailsError || undefined} />
        <FullWidthButton
          type="submit"
          disabled={savingDetails}
          aria-busy={savingDetails}
        >
          <StatusLabel
            state={savingDetails ? 'busy' : 'idle'}
            labels={{
              idle: (
                <>
                  Save details <RightArrow />
                </>
              ),
              busy: 'Saving…',
            }}
          />
        </FullWidthButton>
      </form>

      <SectionHeading style={{ marginTop: 32 }}>Email address</SectionHeading>
      <SectionLead>
        This is what you sign in with, so a new address has to be confirmed
        before it takes effect. Your current one keeps working until then.
      </SectionLead>

      {profile?.pending_email ? (
        <PendingBanner>
          <span>
            Waiting for <strong>{profile.pending_email}</strong> to be confirmed.
            Open the link we sent there — until you do, you still sign in with{' '}
            {profile.email}.
          </span>
          <QuietButton type="button" onClick={cancelPending}>
            Cancel this change
          </QuietButton>
        </PendingBanner>
      ) : null}

      <form onSubmit={submitEmail}>
        <Field>
          <Label htmlFor="ap-email">Email</Label>
          <Shake active={!!emailError} attempt={emailAttempt}>
            <Input
              id="ap-email"
              type="email"
              value={email}
              maxLength={160}
              autoComplete="email"
              hasError={!!emailError}
              onChange={e => setEmail(e.target.value)}
            />
          </Shake>
        </Field>
        <Field>
          <Label htmlFor="ap-email-pw">Current password</Label>
          <Shake active={!!emailError} attempt={emailAttempt}>
            <PasswordInput
              id="ap-email-pw"
              type="password"
              value={emailPassword}
              autoComplete="current-password"
              hasError={!!emailError}
              placeholder="Required to move the account"
              onChange={e => setEmailPassword(e.target.value)}
            />
          </Shake>
        </Field>
        <FieldError message={emailError || undefined} />
        <FullWidthButton
          type="submit"
          disabled={savingEmail}
          aria-busy={savingEmail}
        >
          <StatusLabel
            state={savingEmail ? 'busy' : 'idle'}
            labels={{
              idle: (
                <>
                  Send confirmation <RightArrow />
                </>
              ),
              busy: 'Sending…',
            }}
          />
        </FullWidthButton>
      </form>

      <HelperText>
        {profile?.email_verified
          ? 'Your email address is confirmed.'
          : 'Your email address has not been confirmed yet. Check your inbox for the verification link.'}
      </HelperText>
    </>
  );
};
